import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Award, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';

export const Hero = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img src="https://customer-assets.emergentagent.com/job_a13cc131-3351-41ce-87d0-56be3a0fcef6/artifacts/rs7tfz6i_PHOTO-2026-04-10-17-15-01.jpg" alt="Gipser Beni" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="inline-flex items-center space-x-3 bg-[#E31C23] text-white px-6 py-3 mb-8">
              <Award className="w-6 h-6" />
              <span className="text-lg font-bold">{t.hero.experience_badge}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-title">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-lg md:text-xl text-white/80 leading-relaxed mb-10 max-w-2xl" data-testid="hero-subtitle">{t.hero.subtitle}</motion.p>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col sm:flex-row gap-4">
              <Link to="/kontakt" className="group inline-flex items-center justify-center space-x-2 bg-[#E31C23] text-white px-8 py-4 text-base font-semibold hover:bg-[#B91219] transition-all duration-300" data-testid="hero-cta-primary">
                <span>{t.hero.cta_primary}</span><ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/referenzen" className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 text-base font-semibold hover:bg-white/20 transition-all" data-testid="hero-cta-secondary">
                <Play className="w-5 h-5" /><span>{t.hero.cta_secondary}</span>
              </Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8">
              <div className="text-center mb-6">
                <img src="https://customer-assets.emergentagent.com/job_a13cc131-3351-41ce-87d0-56be3a0fcef6/artifacts/sac0lcc8_Logo.png" alt="BG Logo" className="h-32 mx-auto mb-4" />
                <div className="text-lg text-white/70 italic mt-3">Vielfalt Planung. Vielfalt Raum.</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-4 bg-white/10">
                  <div className="font-heading text-2xl font-bold text-[#E31C23]">5</div>
                  <div className="text-sm text-white/80">{language === 'de' ? 'Dienstleistungen' : 'Services'}</div>
                </div>
                <div className="text-center p-4 bg-white/10">
                  <div className="font-heading text-2xl font-bold text-[#00A651]">10+</div>
                  <div className="text-sm text-white/80">{language === 'de' ? 'Gemeinden' : 'Municipalities'}</div>
                </div>
              </div>
              <p className="text-white/80 text-sm text-center leading-relaxed">{t.hero.founder_text}</p>
              <Link to="/team" className="mt-6 flex items-center justify-center space-x-2 w-full bg-white text-[#1A1A1A] px-6 py-3 font-semibold hover:bg-gray-100 transition-colors" data-testid="hero-about-link">
                <span>{t.hero.view_reviews}</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center space-y-2">
          <span className="text-white/60 text-xs uppercase tracking-widest">{t.hero.discover_more}</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"><div className="w-1.5 h-3 bg-white/60 rounded-full" /></motion.div>
        </div>
      </motion.div>
    </section>
  );
};
